/**
 * DeviceModalController - Device add/edit modal management
 * 
 * Handles:
 * - Add device modal (showAddDeviceModal)
 * - Edit device modal (showEditDeviceModal, showEditTemplateModal)
 * - Port group list rendering and manipulation
 * - Multi-type editor for signal types
 * - Connector type options
 * - Save device/template logic
 */

import { escapeHtml, generateId } from '../utils/index.js';
import { CONNECTOR_TYPES, MULTI_TYPE_PRESETS } from '../constants/index.js';
import { getDefaultConnector, normalizeSignalTypes } from '../services/ConnectionService.js';
import { getConnectorsForSignalType } from '../data/connectorMappings.js';
import { showToast } from './ToastController.js';
import { getContextMenuSpawnPosition, clearContextMenuSpawnPosition } from './ContextMenuController.js';
import { showConfirmModal } from './ModalController.js';

// ============= STATE =============
let modalPortGroups = [];
let editingInstanceId = null;
let expandedGroups = new Set();
let multiTypeEditorIdx = null;
let multiTypeSelectedTypes = [];

// DOM reference (set during init)
let modal = null;

// ============= CALLBACKS =============
let callbacks = {
    instanceModel: null,
    definitionModel: null,
    connectionModel: null,
    getTemplateForDevice: null,
    getConnectionSourceGroup: null,
    getConnectionTargetGroup: null,
    createDevice: null,
    rerenderAllDevicesForTemplate: null,
    renderDevice: null,
    renderAllCables: null,
    updateConnectedPorts: null,
    renderLibrary: null,
    scheduleAutosave: null,
    recordAction: null
};

/**
 * Initialize the device modal controller
 * @param {Object} config - Configuration with callbacks
 */
export function initDeviceModalController(config) {
    callbacks = { ...callbacks, ...config };
    modal = document.getElementById('device-modal');
    
    // Handle Escape key for closing modals
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            // First check if multitype editor is open
            const multitypeOverlay = document.getElementById('multitype-overlay');
            if (multitypeOverlay) {
                e.preventDefault();
                e.stopPropagation();
                closeMultiTypeEditor();
                return;
            }
            
            // Then check if device modal is open
            if (modal && modal.classList.contains('active')) {
                e.preventDefault();
                e.stopPropagation();
                closeModal();
                return;
            }
        }
    });
}

/**
 * Get current modal port groups (for external access if needed)
 */
export function getModalPortGroups() {
    return modalPortGroups;
}

/**
 * Get editing instance ID (for external access if needed)
 */
export function getEditingInstanceId() {
    return editingInstanceId;
}

// ============= MODAL FUNCTIONS =============

/**
 * Show the add device modal with default port groups
 */
export function showAddDeviceModal() {
    document.getElementById('modal-title').textContent = 'Add Device';
    document.getElementById('device-edit-id').value = '';
    document.getElementById('device-template-id').value = '';
    document.getElementById('device-name').value = '';
    document.getElementById('device-manufacturer').value = '';
    document.getElementById('device-version').value = '';
    document.getElementById('device-is-router').checked = false;
    document.getElementById('instance-warning').style.display = 'none';
    document.getElementById('btn-save-as-new').style.display = 'none';
    editingInstanceId = null;
    
    // Default groups for a typical device (empty names - display falls back to signal type)
    modalPortGroups = [
        { name: '', direction: 'output', type: 'audio', count: 2 },
        { name: '', direction: 'input', type: 'audio', count: 2 },
        { name: '', direction: 'input', type: 'midi', count: 1 },
        { name: '', direction: 'output', type: 'midi', count: 1 }
    ];
    
    renderPortGroupsList();
    modal.classList.add('active');
    document.getElementById('device-name').focus();
}

/**
 * Show the edit device modal for an existing device instance
 * @param {Object} device - The device instance to edit
 */
export function showEditDeviceModal(device) {
    const template = callbacks.getTemplateForDevice?.(device);
    if (!template) return;
    
    document.getElementById('modal-title').textContent = 'Edit Device';
    document.getElementById('device-edit-id').value = device.id;
    document.getElementById('device-template-id').value = template.id;
    document.getElementById('device-name').value = template.name;
    document.getElementById('device-manufacturer').value = template.manufacturer || '';
    document.getElementById('device-version').value = template.version || '';
    document.getElementById('device-is-router').checked = !!template.isRouter;
    editingInstanceId = device.id;
    
    // Show instance warning if there are multiple instances
    const instanceCount = callbacks.instanceModel?.getByTemplateId(template.id).length || 0;
    const warning = document.getElementById('instance-warning');
    if (instanceCount > 1) {
        document.getElementById('instance-count').textContent = instanceCount;
        warning.style.display = 'block';
        document.getElementById('btn-save-as-new').style.display = '';
    } else {
        warning.style.display = 'none';
        document.getElementById('btn-save-as-new').style.display = 'none';
    }
    
    // Clone the template's port groups
    modalPortGroups = template.portGroups.map(g => ({...g}));
    
    renderPortGroupsList();
    modal.classList.add('active');
    document.getElementById('device-name').focus();
}

/**
 * Show the edit template modal (from library)
 * @param {Object} template - The template to edit
 */
export function showEditTemplateModal(template) {
    document.getElementById('modal-title').textContent = 'Edit Template';
    document.getElementById('device-edit-id').value = '';
    document.getElementById('device-template-id').value = template.id;
    document.getElementById('device-name').value = template.name;
    document.getElementById('device-manufacturer').value = template.manufacturer || '';
    document.getElementById('device-version').value = template.version || '';
    document.getElementById('device-is-router').checked = !!template.isRouter;
    editingInstanceId = null;
    
    // Show instance warning
    const instanceCount = callbacks.instanceModel?.getByTemplateId(template.id).length || 0;
    const warning = document.getElementById('instance-warning');
    if (instanceCount > 0) {
        document.getElementById('instance-count').textContent = instanceCount;
        warning.style.display = 'block';
    } else {
        warning.style.display = 'none';
    }
    document.getElementById('btn-save-as-new').style.display = 'none';
    
    // Clone the template's port groups
    modalPortGroups = template.portGroups.map(g => ({...g}));
    
    renderPortGroupsList();
    modal.classList.add('active');
    document.getElementById('device-name').focus();
}

// ============= PORT GROUP RENDERING =============

/**
 * Build connector type options HTML, filtered by signal type
 * @param {string} selectedConnector - Currently selected connector type
 * @param {string|string[]} signalType - Signal type(s) to filter connectors for
 * @returns {string} HTML options string
 */
function buildConnectorOptions(selectedConnector, signalType) {
    const options = ['<option value="">— None —</option>'];
    const allowedConnectors = getConnectorsForSignalType(signalType);
    
    for (const [key, info] of Object.entries(CONNECTOR_TYPES)) {
        // If we have a filter list, skip connectors not in it
        if (allowedConnectors && !allowedConnectors.includes(key)) {
            continue;
        }
        const selected = selectedConnector === key ? 'selected' : '';
        options.push(`<option value="${key}" ${selected}>${info.label}</option>`);
    }
    return options.join('');
}

/**
 * Build signal type options HTML (supports multi-type)
 * @param {string|string[]} selectedTypes - Currently selected type(s)
 * @param {boolean} isMultiType - Whether multiple types are selected
 * @returns {string} HTML options string
 */
function buildTypeOptions(selectedTypes, isMultiType = false) {
    const types = normalizeSignalTypes(selectedTypes);
    const primaryType = types[0] || 'audio';
    
    // If multi-type, show "multi" as selected and don't show other options as selected
    if (isMultiType) {
        return `
            <option value="multi" selected>🔀 Multi</option>
            <option value="audio">🔊 Audio</option>
            <option value="midi">🎹 MIDI</option>
            <option value="midi-rtp">🌐 MIDI RTP</option>
            <option value="midi-usb-host">🔌 USB Host</option>
            <option value="midi-usb-device">📱 USB Dev</option>
            <option value="cv">⚡ CV</option>
            <option value="gate">🚪 Gate</option>
            <option value="clock">⏱️ Clock</option>
        `;
    }
    
    return `
        <option value="multi">🔀 Multi</option>
        <option value="audio" ${primaryType === 'audio' ? 'selected' : ''}>🔊 Audio</option>
        <option value="midi" ${primaryType === 'midi' ? 'selected' : ''}>🎹 MIDI</option>
        <option value="midi-rtp" ${primaryType === 'midi-rtp' ? 'selected' : ''}>🌐 MIDI RTP</option>
        <option value="midi-usb-host" ${primaryType === 'midi-usb-host' ? 'selected' : ''}>🔌 USB Host</option>
        <option value="midi-usb-device" ${primaryType === 'midi-usb-device' ? 'selected' : ''}>📱 USB Dev</option>
        <option value="cv" ${primaryType === 'cv' ? 'selected' : ''}>⚡ CV</option>
        <option value="gate" ${primaryType === 'gate' ? 'selected' : ''}>🚪 Gate</option>
        <option value="clock" ${primaryType === 'clock' ? 'selected' : ''}>⏱️ Clock</option>
    `;
}

/**
 * Render the port groups list in the modal
 */
export function renderPortGroupsList() {
    const list = document.getElementById('port-groups-list');
    
    if (modalPortGroups.length === 0) {
        list.innerHTML = '<div class="empty-groups">No port groups. Click "Add Group" to create one.</div>';
        return;
    }
    
    list.innerHTML = modalPortGroups.map((group, idx) => {
        const isExpanded = expandedGroups.has(idx);
        const hasAdvanced = (group.offset && group.offset !== 1) || group.labels || group.stereoPairs || group.name || group.types;
        const types = normalizeSignalTypes(group.types || group.type);
        const hasMultiTypes = types.length > 1;
        
        return `
        <div class="port-group-row ${isExpanded ? 'expanded' : ''}" data-idx="${idx}">
            <select onchange="updatePortGroup(${idx}, 'direction', this.value)">
                <option value="input" ${group.direction === 'input' ? 'selected' : ''}>Input</option>
                <option value="output" ${group.direction === 'output' ? 'selected' : ''}>Output</option>
                <option value="insert" ${group.direction === 'insert' ? 'selected' : ''}>↔ Insert</option>
            </select>
            <div class="type-dropdown-container${hasMultiTypes ? ' has-multi' : ''}">
                <select class="type-dropdown${hasMultiTypes ? ' multi-active' : ''}" onchange="updatePortGroup(${idx}, 'type', this.value)" title="Signal type${hasMultiTypes ? ': ' + types.join(', ') : ''}">
                    ${buildTypeOptions(group.types || group.type, hasMultiTypes)}
                </select>${hasMultiTypes ? `<button type="button" class="btn-multi-edit" onclick="showMultiTypeEditor(${idx})" title="Edit multi-type selection: ${types.join(', ')}">✏️</button>` : ''}
            </div>
            <select class="connector-dropdown" onchange="updatePortGroup(${idx}, 'connector', this.value)" title="Physical connector type">
                ${buildConnectorOptions(group.connector, group.types || group.type)}
            </select>
            <input type="number" value="${group.count}" min="1" max="128" onchange="updatePortGroup(${idx}, 'count', this.value)" title="Total channel count">
            <button class="btn-toggle-advanced ${isExpanded || hasAdvanced ? 'expanded' : ''}" onclick="toggleGroupAdvanced(${idx})" title="Group name & channel options">⚙️</button>
            <button class="btn-remove-group" onclick="removePortGroup(${idx})">×</button>
            ${isExpanded ? `
            <div class="port-group-advanced">
                <div class="port-group-compact" style="flex:1">
                    <label>Group Name</label>
                    <input type="text" value="${escapeHtml(group.name)}" placeholder="Optional group name" onchange="updatePortGroup(${idx}, 'name', this.value)" title="Custom name for this port group">
                </div>
                <div class="port-group-compact">
                    <label>Offset</label>
                    <input type="number" class="small" value="${group.offset || 1}" min="0" max="999" onchange="updatePortGroup(${idx}, 'offset', this.value)" title="Starting channel number">
                </div>
                <div class="port-group-compact">
                    <label style="display:flex;align-items:center;gap:4px;cursor:pointer">
                        <input type="checkbox" ${group.stereoPairs ? 'checked' : ''} onchange="updatePortGroup(${idx}, 'stereoPairs', this.checked)" title="Label as stereo pairs (1L, 1R, 2L, 2R...)">
                        Stereo pairs
                    </label>
                </div>
                <div class="port-group-compact" style="flex:1">
                    <label>Labels</label>
                    <input type="text" value="${escapeHtml(group.labels || '')}" placeholder="left,right,center..." onchange="updatePortGroup(${idx}, 'labels', this.value)" title="Custom labels (comma-separated)">
                </div>
                ${hasMultiTypes ? `
                <div class="port-group-compact" style="width:100%;margin-top:4px;">
                    <label style="color:#a855f7;">Multi-type: ${types.join(', ')}</label>
                </div>
                ` : `
                <div class="port-group-compact" style="margin-top:4px;">
                    <label style="display:flex;align-items:center;gap:4px;cursor:pointer">
                        <button type="button" class="btn-toggle-advanced" onclick="showMultiTypeEditor(${idx})" title="Add additional signal types">+ Multi-type</button>
                    </label>
                </div>
                `}
            </div>
            ` : ''}
        </div>
    `}).join('');
}

// ============= MULTI-TYPE EDITOR =============

/**
 * Show multi-type editor modal for a port group
 * @param {number} idx - Port group index
 */
export function showMultiTypeEditor(idx) {
    const group = modalPortGroups[idx];
    if (!group) return;
    
    multiTypeEditorIdx = idx;
    multiTypeSelectedTypes = [...normalizeSignalTypes(group.types || group.type)];
    
    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.id = 'multitype-overlay';
    overlay.className = 'modal-overlay active';
    overlay.style.zIndex = '1001'; // Above the main modal
    overlay.innerHTML = `
        <div class="modal multitype-modal">
            <h2>Select Signal Types</h2>
            <p style="color:#8892b0;margin-bottom:16px;font-size:0.9rem;">
                Multi-type ports accept any of the selected signal types.
            </p>
            
            <h3>Quick Presets</h3>
            <div class="multitype-presets" id="multitype-presets"></div>
            
            <h3>Signal Types</h3>
            <div class="multitype-checkboxes" id="multitype-checkboxes"></div>
            
            <div class="multitype-preview">
                <div class="multitype-preview-label">Selected types:</div>
                <div class="multitype-preview-types" id="multitype-preview"></div>
            </div>
            
            <div class="modal-buttons">
                <button class="btn-secondary" onclick="closeMultiTypeEditor()">Cancel</button>
                <button class="btn-primary" onclick="applyMultiTypeEditor()">Apply</button>
            </div>
        </div>
    `;
    
    document.body.appendChild(overlay);
    renderMultiTypeEditor();
}

/**
 * Render the multi-type editor content
 */
function renderMultiTypeEditor() {
    const allTypes = [
        { id: 'audio', icon: '🔊', label: 'Audio' },
        { id: 'midi', icon: '🎹', label: 'MIDI' },
        { id: 'cv', icon: '⚡', label: 'CV' },
        { id: 'gate', icon: '🚪', label: 'Gate' },
        { id: 'clock', icon: '⏱️', label: 'Clock' }
    ];
    
    // Render presets
    const presetsContainer = document.getElementById('multitype-presets');
    if (presetsContainer) {
        presetsContainer.innerHTML = Object.entries(MULTI_TYPE_PRESETS).map(([key, preset]) => {
            const isActive = arraysEqual(multiTypeSelectedTypes.sort(), [...preset.types].sort());
            return `<button class="multitype-preset-btn ${isActive ? 'active' : ''}" 
                            onclick="applyMultiTypePreset('${key}')" 
                            title="${preset.description}">
                ${preset.label}
            </button>`;
        }).join('');
    }
    
    // Render checkboxes
    // Note: Using <div> instead of <label> to avoid label-checkbox click interference
    // when the checkbox is hidden. The onclick handler handles all toggle logic.
    const checkboxesContainer = document.getElementById('multitype-checkboxes');
    if (checkboxesContainer) {
        checkboxesContainer.innerHTML = allTypes.map(t => {
            const checked = multiTypeSelectedTypes.includes(t.id);
            return `<div class="multitype-checkbox ${checked ? 'checked' : ''}" onclick="toggleMultiType('${t.id}')">
                <span class="checkbox-visual"></span>
                <span class="type-icon">${t.icon}</span>
                <span class="type-label">${t.label}</span>
            </div>`;
        }).join('');
    }
    
    // Render preview
    const previewContainer = document.getElementById('multitype-preview');
    if (previewContainer) {
        if (multiTypeSelectedTypes.length === 0) {
            previewContainer.innerHTML = '<span style="color:#666;">None selected (will default to Audio)</span>';
        } else {
            previewContainer.innerHTML = multiTypeSelectedTypes.map(t => 
                `<span class="multitype-preview-type ${t}">${t}</span>`
            ).join('');
        }
    }
}

/**
 * Check if two arrays are equal
 */
function arraysEqual(a, b) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
}

/**
 * Toggle a signal type in the multi-type editor
 * @param {string} typeId - The type to toggle
 */
export function toggleMultiType(typeId) {
    const idx = multiTypeSelectedTypes.indexOf(typeId);
    if (idx >= 0) {
        multiTypeSelectedTypes.splice(idx, 1);
    } else {
        multiTypeSelectedTypes.push(typeId);
    }
    renderMultiTypeEditor();
}

/**
 * Apply a multi-type preset
 * @param {string} presetKey - The preset key to apply
 */
export function applyMultiTypePreset(presetKey) {
    const preset = MULTI_TYPE_PRESETS[presetKey];
    if (preset) {
        multiTypeSelectedTypes = [...preset.types];
        renderMultiTypeEditor();
    }
}

/**
 * Close the multi-type editor
 */
export function closeMultiTypeEditor() {
    const overlay = document.getElementById('multitype-overlay');
    if (overlay) overlay.remove();
    multiTypeEditorIdx = null;
    multiTypeSelectedTypes = [];
}

/**
 * Apply the multi-type editor selections
 */
export function applyMultiTypeEditor() {
    if (multiTypeEditorIdx === null) return;
    
    let selected = multiTypeSelectedTypes;
    if (selected.length === 0) {
        selected = ['audio']; // Default fallback
    }
    
    const group = modalPortGroups[multiTypeEditorIdx];
    const currentConnector = group.connector;
    
    // Update the group
    if (selected.length === 1) {
        group.type = selected[0];
        delete group.types;
    } else {
        group.types = selected;
        group.type = selected[0]; // Primary type
    }
    
    // Check if current connector is still valid for new type(s)
    const signalType = group.types || group.type;
    const allowedConnectors = getConnectorsForSignalType(signalType);
    if (currentConnector && allowedConnectors && !allowedConnectors.includes(currentConnector)) {
        // Connector no longer valid, set to default for primary type
        group.connector = getDefaultConnector(selected[0]);
    }
    
    closeMultiTypeEditor();
    renderPortGroupsList();
}

// ============= PORT GROUP MANIPULATION =============

/**
 * Toggle advanced options panel for a port group
 * @param {number} idx - Port group index
 */
export function toggleGroupAdvanced(idx) {
    if (expandedGroups.has(idx)) {
        expandedGroups.delete(idx);
    } else {
        expandedGroups.add(idx);
    }
    renderPortGroupsList();
}

/**
 * Add a new port group row
 */
export function addPortGroupRow() {
    const defaultType = 'audio';
    modalPortGroups.push({
        name: '',
        direction: 'output',
        type: defaultType,
        count: 1,
        connector: getDefaultConnector(defaultType)
    });
    renderPortGroupsList();
}

/**
 * Update a port group field
 * @param {number} idx - Port group index
 * @param {string} field - Field name to update
 * @param {*} value - New value
 */
export async function updatePortGroup(idx, field, value) {
    if (modalPortGroups[idx]) {
        if (field === 'count') {
            modalPortGroups[idx][field] = Math.max(1, Math.min(128, parseInt(value) || 1));
        } else if (field === 'offset') {
            modalPortGroups[idx][field] = Math.max(0, parseInt(value) || 1);
        } else if (field === 'stereoPairs') {
            modalPortGroups[idx][field] = !!value;
        } else if (field === 'labels') {
            if (value && value.trim()) {
                modalPortGroups[idx][field] = value.trim();
            } else {
                delete modalPortGroups[idx][field];
            }
        } else if (field === 'connector') {
            // Handle connector type selection
            if (value && value.trim()) {
                modalPortGroups[idx].connector = value;
                
                // Auto-suggest channel count for multi-channel connectors
                const connectorInfo = CONNECTOR_TYPES[value];
                if (connectorInfo && connectorInfo.subgroupSize) {
                    const currentCount = modalPortGroups[idx].count || 1;
                    const suggestedCount = connectorInfo.subgroupSize;
                    
                    // Only suggest if current count is 1 or a generic small number
                    if (currentCount === 1 || currentCount < suggestedCount) {
                        const doUpdate = await showConfirmModal({
                            title: 'Update Channel Count',
                            message: `${connectorInfo.label} typically has ${suggestedCount} channels.\n\nUpdate channel count from ${currentCount} to ${suggestedCount}?`,
                            confirmText: 'Update',
                            cancelText: 'Keep Current'
                        });
                        if (doUpdate) {
                            modalPortGroups[idx].count = suggestedCount;
                            renderPortGroupsList();
                            return; // Already re-rendered
                        }
                    }
                }
            } else {
                delete modalPortGroups[idx].connector;
            }
        } else if (field === 'type') {
            // Handle 'multi' selection - open multi-type editor
            if (value === 'multi') {
                showMultiTypeEditor(idx);
                return; // Don't update the type directly
            }
            
            // When changing primary type, clear multi-types
            const oldType = modalPortGroups[idx].type;
            const oldDefaultConnector = getDefaultConnector(oldType);
            const currentConnector = modalPortGroups[idx].connector;
            
            modalPortGroups[idx].type = value;
            delete modalPortGroups[idx].types;
            
            // Check if current connector is valid for the new type
            const allowedConnectors = getConnectorsForSignalType(value);
            const connectorStillValid = !allowedConnectors || !currentConnector || allowedConnectors.includes(currentConnector);
            
            // Update connector if it was the default for the old type, or if it's no longer valid
            if (!currentConnector || currentConnector === oldDefaultConnector || !connectorStillValid) {
                modalPortGroups[idx].connector = getDefaultConnector(value);
            }
            
            // Re-render to update the UI (multi button will disappear, connector options will filter)
            renderPortGroupsList();
        } else {
            modalPortGroups[idx][field] = value;
        }
    }
}

/**
 * Remove a port group
 * @param {number} idx - Port group index
 */
export function removePortGroup(idx) {
    modalPortGroups.splice(idx, 1);
    renderPortGroupsList();
}

// ============= MODAL CLOSE =============

/**
 * Close the device modal
 */
export function closeModal() {
    modal.classList.remove('active');
    modalPortGroups = [];
    editingInstanceId = null;
    expandedGroups.clear();
    clearContextMenuSpawnPosition(); // Clear spawn position on cancel
}

// ============= SAVE FUNCTIONS =============

/**
 * Save the device (create new or update existing)
 */
export function saveDevice() {
    const name = document.getElementById('device-name').value.trim();
    const manufacturer = document.getElementById('device-manufacturer').value.trim();
    const version = document.getElementById('device-version').value.trim();
    const isRouter = document.getElementById('device-is-router').checked;
    
    if (!name) {
        showToast('Please enter a device name', true);
        return;
    }

    const templateId = document.getElementById('device-template-id').value;
    
    // Normalize port groups
    const normalizedGroups = modalPortGroups.map(g => {
        const normalized = {
            name: g.name,
            direction: g.direction,
            type: g.types && g.types.length > 1 ? g.types : (g.type || 'audio'),
            count: parseInt(g.count) || 1,
        };
        // Optional fields
        if (g.offset && g.offset !== 1) normalized.offset = parseInt(g.offset);
        if (g.labels) normalized.labels = g.labels;
        if (g.stereoPairs) normalized.stereoPairs = true;
        if (g.connector) normalized.connector = g.connector;
        return normalized;
    });
    const expandedGroups = normalizedGroups;

    if (templateId) {
        // Edit existing template
        const template = callbacks.definitionModel?.getById(templateId);
        if (template) {
            // Capture state before changes for undo
            const oldTemplate = JSON.parse(JSON.stringify(template));
            const oldConnections = JSON.parse(JSON.stringify(callbacks.connectionModel?.getAll() || []));
            
            template.name = name;
            template.manufacturer = manufacturer;
            template.version = version;
            template.isRouter = isRouter;
            
            // Debug: log isRouter changes
            if (isRouter !== oldTemplate.isRouter) {
                console.log('[Template] isRouter changed:', oldTemplate.isRouter, '→', isRouter, 
                    '| template:', template.id, template.name);
            }
            
            // Build map of valid group indices and their counts
            const newGroupCount = expandedGroups.length;
            const groupCountMap = {};
            expandedGroups.forEach((g, idx) => {
                groupCountMap[idx] = parseInt(g.count) || 1;
            });
            
            // Remove connections referencing removed groups or out-of-range ports
            const affectedDeviceIds = (callbacks.instanceModel?.getByTemplateId(templateId) || []).map(d => d.id);
            callbacks.connectionModel?.replaceAll(callbacks.connectionModel.filter(c => {
                if (affectedDeviceIds.includes(c.sourceDeviceId)) {
                    const srcGroup = callbacks.getConnectionSourceGroup?.(c);
                    if (srcGroup >= newGroupCount) return false;
                    if (c.sourcePortIndex > groupCountMap[srcGroup]) return false;
                }
                if (affectedDeviceIds.includes(c.targetDeviceId)) {
                    const tgtGroup = callbacks.getConnectionTargetGroup?.(c);
                    if (tgtGroup >= newGroupCount) return false;
                    if (c.targetPortIndex > groupCountMap[tgtGroup]) return false;
                }
                return true;
            }));
            
            const newPortGroups = expandedGroups.map(g => {
                const pg = {
                    name: g.name,
                    direction: g.direction,
                    type: g.type,
                    count: parseInt(g.count) || 1,
                };
                if (g.offset) pg.offset = g.offset;
                if (g.labels) pg.labels = g.labels;
                if (g.stereoPairs) pg.stereoPairs = g.stereoPairs;
                if (g.connector) pg.connector = g.connector;
                return pg;
            });
            template.portGroups = newPortGroups;

            // Re-render all instances of this template
            callbacks.rerenderAllDevicesForTemplate?.(templateId);
            callbacks.renderLibrary?.();
            callbacks.scheduleAutosave?.();
            
            // Record action for undo/redo
            const newTemplate = JSON.parse(JSON.stringify(template));
            const newConnections = JSON.parse(JSON.stringify(callbacks.connectionModel?.getAll() || []));
            
            callbacks.recordAction?.(
                'edit-template',
                `Edit ${name}`,
                // Undo: restore old template and connections
                () => {
                    const t = callbacks.definitionModel?.getById(templateId);
                    if (t) {
                        Object.assign(t, oldTemplate);
                    }
                    callbacks.connectionModel?.replaceAll(JSON.parse(JSON.stringify(oldConnections)));
                    callbacks.rerenderAllDevicesForTemplate?.(templateId);
                    callbacks.renderAllCables?.();
                    callbacks.updateConnectedPorts?.();
                    callbacks.renderLibrary?.();
                    callbacks.scheduleAutosave?.();
                },
                // Redo: apply new template and connections
                () => {
                    const t = callbacks.definitionModel?.getById(templateId);
                    if (t) {
                        Object.assign(t, newTemplate);
                    }
                    callbacks.connectionModel?.replaceAll(JSON.parse(JSON.stringify(newConnections)));
                    callbacks.rerenderAllDevicesForTemplate?.(templateId);
                    callbacks.renderAllCables?.();
                    callbacks.updateConnectedPorts?.();
                    callbacks.renderLibrary?.();
                    callbacks.scheduleAutosave?.();
                }
            );
            
            showToast('Template updated');
        }
    } else {
        // Create new device (template + instance)
        // Use stored position from context menu if available
        const spawnPosition = getContextMenuSpawnPosition();
        clearContextMenuSpawnPosition(); // Clear after use
        callbacks.createDevice?.(name, expandedGroups, spawnPosition, manufacturer, version, isRouter);
        showToast('Device added');
    }

    closeModal();
}

/**
 * Save device as a new variant (creates new template)
 */
export function saveDeviceAsNew() {
    const name = document.getElementById('device-name').value.trim();
    const manufacturer = document.getElementById('device-manufacturer').value.trim();
    const version = document.getElementById('device-version').value.trim();
    const isRouter = document.getElementById('device-is-router').checked;
    
    if (!name) {
        showToast('Please enter a device name', true);
        return;
    }
    
    // Create new template
    const newTemplate = {
        id: generateId(),
        name: name,
        manufacturer: manufacturer,
        version: version,
        isRouter: isRouter,
        portGroups: modalPortGroups.map(g => {
            const pg = {
                name: g.name,
                direction: g.direction,
                type: g.types && g.types.length > 1 ? g.types : (g.type || 'audio'),
                count: parseInt(g.count) || 1,
            };
            if (g.offset && g.offset !== 1) pg.offset = parseInt(g.offset);
            if (g.labels) pg.labels = g.labels;
            if (g.stereoPairs) pg.stereoPairs = true;
            if (g.connector) pg.connector = g.connector;
            return pg;
        })
    };
    callbacks.definitionModel?.add(newTemplate);
    
    // Update the current instance to use the new template
    if (editingInstanceId) {
        const device = callbacks.instanceModel?.getById(editingInstanceId);
        if (device) {
            device.templateId = newTemplate.id;
            
            // Remove connections from this device (port groups changed)
            callbacks.connectionModel?.removeByDeviceId(device.id);
            
            callbacks.renderDevice?.(device);
            callbacks.renderAllCables?.();
        }
    }
    
    callbacks.renderLibrary?.();
    callbacks.scheduleAutosave?.();
    closeModal();
    showToast('Saved as new variant');
}
